"use client";

import { useState } from "react";
import classNames from "classnames";
import Button from "./Button";
import FontAwesome from "./FontAwesome";
import ImageComponent from "./ImageComponent";
import styles from "./styles/PhotoCarousel.module.scss";

interface PhotoCarouselProps {
  images: MediaImage[];
  alt: string;
  className?: string;
  showThumbnails?: boolean;
  onImageClick?: () => void;
}

export default function PhotoCarousel(props: PhotoCarouselProps) {
  const [index, setIndex] = useState(0);
  const count = props.images.length;
  const step = (delta: number) => setIndex((i) => (i + delta + count) % count);

  if (count === 0) {
    return null;
  }

  const image = props.images[index] ?? props.images[0];
  const photo = (
    <ImageComponent
      key={image.raw_url}
      src={image.raw_url}
      alt={image.alt_text || props.alt}
    />
  );

  return (
    <div className={classNames(styles.carousel, props.className)}>
      {props.onImageClick ? (
        // A click handler on the image alone is reachable by pointer only. The
        // alt text names the button, so it needs no label of its own.
        <Button
          type="button"
          className={styles.photoButton}
          onClick={props.onImageClick}
        >
          {photo}
        </Button>
      ) : (
        photo
      )}
      <div className={styles.controls}>
        <Button
          type="button"
          aria-label="Previous photo"
          className={styles.arrow}
          onClick={() => step(-1)}
        >
          <FontAwesome prefix="fas" name="fa-chevron-left" />
        </Button>
        <Button
          type="button"
          aria-label="Next photo"
          className={styles.arrow}
          onClick={() => step(1)}
        >
          <FontAwesome prefix="fas" name="fa-chevron-right" />
        </Button>
        <span className={styles.counter} aria-live="polite">
          {index + 1} of {count}
        </span>
      </div>
      {props.showThumbnails && (
        <div className={styles.thumbnails}>
          {props.images.map((image, i) => (
            <Button
              key={`${image.raw_url}-${i}`}
              type="button"
              aria-label={`Show photo ${i + 1}`}
              aria-current={i === index}
              className={
                i === index
                  ? `${styles.thumbnail} ${styles.thumbnailActive}`
                  : styles.thumbnail
              }
              onClick={() => setIndex(i)}
            >
              <ImageComponent src={image.raw_url} alt="" />
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
